import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, filter, finalize, Subject, take, takeUntil } from 'rxjs';
import { LogoutService } from 'src/app/auth/logout.service';
import { PAGE_SIZE_OPTIONS, PROJECT_TITLE } from 'src/app/common/constant/constants.type';
import { MaterialModule } from 'src/app/material.module';
import { PaginationRequest } from 'src/app/models/common.request';
import { PaginationResult } from 'src/app/models/common.response';
import { AppointmentDialog } from '../dialogs/confirmation-appointment/appointment-dialog.component';
import { HospitalDetailComponent } from './detail/hospital-detail.component';
import {
  HospitalService,
  MigrateLegacyDocumentsResult
} from './hospital.service';
import { IHospitalInfo, LegacyDocumentOperationView } from './ihospital-info';

const SEARCH_DEBOUNCE_MS = 300;
const FIRST_PAGE = 0;

type LegacyDocumentOperation = 'copy' | 'delete';

// SuperAdmin console, phase 1: list the hospitals on the platform and onboard a new
// one. Nothing else is exposed - a SuperAdmin has no HospitalId, so the tenant-scoped
// screens (dashboard, OPD, billing) have no meaningful data to show.
@Component({
  selector: 'app-hospital',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    TablerIconsModule,
  ],
  templateUrl: './hospital.component.html',
  styleUrl: './hospital.component.scss'
})
export class HospitalComponent implements OnInit, OnDestroy {
  readonly displayedColumns: string[] = ['name', 'registrationNumber', 'city', 'mobile', 'documentActions'];
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly projectTitle = PROJECT_TITLE;

  dataSource = new MatTableDataSource<IHospitalInfo>();
  pagination: PaginationResult | null = null;
  searchControl = new FormControl('');
  isLoading = false;
  activeDocumentOperation: { hospitalId: number; type: LegacyDocumentOperation } | null = null;
  documentOperationResult: LegacyDocumentOperationView | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private hospitalService: HospitalService,
    private logoutService: LogoutService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.watchHospitals();
    this.watchSearch();
    this.loadHospitals(FIRST_PAGE);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addHospital(): void {
    this.dialog.open(HospitalDetailComponent, {
      height: '80vh',
      minWidth: '70vw',
    }).afterClosed().subscribe(() => {
      this.loadHospitals(FIRST_PAGE);
    });
  }

  confirmLegacyDocumentCopy(hospital: IHospitalInfo): void {
    this.confirmDocumentOperation(
      'Copy legacy patient documents',
      `Copy eligible legacy documents for ${hospital.name} into protected storage? The wwwroot originals will be kept until you delete them separately.`,
      'Copy documents',
      () => this.copyLegacyDocuments(hospital)
    );
  }

  isDocumentOperationRunning(hospitalId: number, type: LegacyDocumentOperation): boolean {
    return this.activeDocumentOperation?.hospitalId === hospitalId
      && this.activeDocumentOperation.type === type;
  }

  get isAnyDocumentOperationRunning(): boolean {
    return this.activeDocumentOperation !== null;
  }

  private confirmDocumentOperation(title: string,message: string,confirmText: string,onConfirm: () => void): void {
    this.dialog.open(AppointmentDialog, {
      width: '520px',
      disableClose: true,
      data: { title, message, cancelText: 'Cancel', confirmText }
    }).afterClosed().pipe(
      take(1),
      filter(result => result === 'yes'),
      takeUntil(this.destroy$)
    ).subscribe(onConfirm);
  }

  onPageChange(event: PageEvent): void {
    this.loadHospitals(event.pageIndex, event.pageSize);
  }

  logout(): void {
    this.logoutService.logout();
  }

  private copyLegacyDocuments(hospital: IHospitalInfo): void {
    this.startDocumentOperation(hospital.id, 'copy');

    this.hospitalService.migrateLegacyDocuments(hospital.id).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.finishDocumentOperation())
    ).subscribe({
      next: response => response.isSuccess
        ? this.showCopyResult(hospital, response.result)
        : this.showOperationError(response.errors, response.message),
      error: () => this.showOperationError([], 'Document copy could not be completed.')
    });
  }

  private startDocumentOperation(hospitalId: number, type: LegacyDocumentOperation): void {
    this.activeDocumentOperation = { hospitalId, type };
    this.documentOperationResult = null;
  }

  private finishDocumentOperation(): void {
    this.activeDocumentOperation = null;
  }

  private showCopyResult(hospital: IHospitalInfo, result: MigrateLegacyDocumentsResult): void {
    this.documentOperationResult = {
      hospitalName: hospital.name,
      title: 'Legacy document copy result',
      found: result.legacyRowsFound,
      completed: result.migrated,
      missing: result.missingFile,
      failed: result.failed,
      errors: result.errors
    };
    this.showResultToast(result.migrated, result.failed, result.errors);
  }

  private showResultToast(completed: number, failed: number, errors: string[]): void {
    if (failed > 0 || errors.length > 0) {
      this.toastr.warning(`${completed} completed, ${failed} failed.`, 'Operation completed with failures');
      return;
    }

    this.toastr.success(`${completed} completed.`, 'Operation completed');
  }

  private showOperationError(errors: string[], message: string): void {
    this.toastr.error(errors.length > 0 ? errors.join('\n') : message);
  }

  private watchHospitals(): void {
    this.hospitalService.hospitals$
      .pipe(takeUntil(this.destroy$))
      .subscribe(hospitals => {
        this.dataSource.data = hospitals;
      });

    this.hospitalService.pagination$
      .pipe(takeUntil(this.destroy$))
      .subscribe(pagination => {
        this.pagination = pagination;
      });
  }

  private watchSearch(): void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadHospitals(FIRST_PAGE);
      });
  }

  private loadHospitals(pageNumber: number, pageSize?: number): void {
    this.isLoading = true;

    const request: PaginationRequest = {
      pageNumber: pageNumber,
      pageSize: pageSize || this.pagination?.pageSize || PAGE_SIZE_OPTIONS[0],
      isDescending: false,
      searchValue: this.searchControl.value?.trim() || ''
    };

    this.hospitalService.getHospital(request)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe();
  }
}
